import React from "react";
import * as Auth from "../lib/Auth";
import { Link, LinkProps } from "react-router-dom";
import Toggle from "react-toggle";
import { default as ReactSelect } from "react-select";
import type { Props as ReactSelectProps } from "react-select";

type Props = React.ButtonHTMLAttributes<HTMLElement> &
    LinkProps & {
        permission: string | boolean;
    };

export function Authorization<T extends HTMLElement>(
    OriginalComponent: React.ComponentType<React.ButtonHTMLAttributes<T>>
);
export function Authorization(OriginalComponent: React.ComponentType<LinkProps>);
export function Authorization(OriginalComponent: ReactSelectProps);
export function Authorization<T extends HTMLElement>(
    OriginalComponent:
        | React.ComponentType<React.ButtonHTMLAttributes<T>>
        | React.ComponentType<LinkProps>
        | React.ComponentType<ReactSelectProps>
) {
    return class extends React.Component<Props> {
        canEdit = (permission: string | boolean) => {
            if (typeof permission === "string") {
                return Auth.hasPermission(permission);
            }

            return permission;
        };

        render() {
            const can_edit = this.canEdit(this.props.permission);
            const name = OriginalComponent.displayName;

            if (name === "Link") {
                const className = this.props.className;
                if (can_edit) {
                    return (
                        <OriginalComponent
                            {...this.props}
                            className={className ? className.replace(" disabled", "") : ""}
                        />
                    );
                } else if (className && !className.includes(" disabled")) {
                    return <OriginalComponent {...this.props} className={className && className + " disabled"} />;
                }
            }

            if (["Button", "Select", "Input", "ToggleWrapper"].includes(name ?? "")) {
                if (can_edit) {
                    const disabled = this.props.disabled;
                    return <OriginalComponent {...this.props} disabled={disabled === undefined ? false : disabled} />;
                } else {
                    return <OriginalComponent {...this.props} disabled={true} />;
                }
            }

            if (name === "AuthReactSelect") {
                return <OriginalComponent {...this.props} isDisabled={this.props.disabled || !can_edit} />;
            }
            // return original component with additional props
            return <OriginalComponent {...this.props} />;
        }
    };
}

const Button = (props: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    return <button {...props}>{props.children}</button>;
};

const Select = (props: React.SelectHTMLAttributes<HTMLSelectElement>) => {
    return <select {...props}>{props.children}</select>;
};

const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => {
    return <input {...props}>{props.children}</input>;
};

const ToggleWrapper = (props: Toggle) => {
    return <Toggle {...props}>{props.children}</Toggle>;
};

const AuthReactSelect = (props: React.SelectHTMLAttributes<HTMLSelectElement>) => {
    return <ReactSelect {...props}>{props.children}</ReactSelect>;
};

// Square brackets are used because displayName does not exist on Link type
Link["displayName"] = "Link";
Authorization.Link = Authorization(Link);
Button.displayName = "Button";
Authorization.button = Authorization<HTMLButtonElement>(Button);
Select.displayName = "Select";
Authorization.select = Authorization<HTMLSelectElement>(Select);
ToggleWrapper.displayName = "ToggleWrapper";
Authorization.Toggle = Authorization<Toggle>(ToggleWrapper);
Input.displayName = "Input";
Authorization.input = Authorization<HTMLInputElement>(Input);
AuthReactSelect.displayName = "AuthReactSelect";
Authorization.Select = Authorization(AuthReactSelect);

export default Authorization;
